/**
 * React Flow host for the Agent Composer. Handles drop-from-palette,
 * selection, snap-to-grid, minimap, and live edge highlighting while a run
 * is in progress.
 */
import { useCallback, useMemo, useRef } from 'react';
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  ConnectionMode,
  type OnSelectionChangeParams,
  type OnNodesChange,
  type OnEdgesChange,
  type OnConnect,
  type ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { composerNodeTypes } from './nodes';
import type { ComposerNode, ComposerEdge } from './useComposerGraph';
import type { ComposerNodeType, NodeRunStatus } from '../../types/composer';

export interface ComposerCanvasProps {
  nodes: ComposerNode[];
  edges: ComposerEdge[];
  onNodesChange: OnNodesChange<ComposerNode>;
  onEdgesChange: OnEdgesChange<ComposerEdge>;
  onConnect: OnConnect;
  onAddNode: (type: ComposerNodeType, position: { x: number; y: number }) => void;
  onSelectionChange: (nodeId: string | null, edgeId: string | null) => void;
  /** Map of nodeId -> live run status used to highlight edges carrying data. */
  nodeStatuses?: Record<string, NodeRunStatus>;
}

const NODE_MINIMAP_COLORS: Record<string, string> = {
  agent: '#2dd4bf',
  condition: '#f59e0b',
  wait: '#3b82f6',
  parallel: '#ec4899',
  merge: '#a78bfa',
};

export const ComposerCanvas = ({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onAddNode,
  onSelectionChange,
  nodeStatuses = {},
}: ComposerCanvasProps) => {
  const reactFlowRef = useRef<ReactFlowInstance<ComposerNode, ComposerEdge> | null>(null);

  const styledEdges = useMemo(
    () =>
      edges.map((edge) => {
        const sourceStatus = nodeStatuses[edge.source];
        const targetStatus = nodeStatuses[edge.target];
        let className = edge.className ?? '';
        if (sourceStatus === 'running' || targetStatus === 'running') {
          className = `${className} cx-edge-live`.trim();
        } else if (sourceStatus === 'succeeded' && targetStatus === 'succeeded') {
          className = `${className} cx-edge-done`.trim();
        }
        return { ...edge, className };
      }),
    [edges, nodeStatuses],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const type = event.dataTransfer.getData('application/composer-node') as ComposerNodeType;
      if (!type || !reactFlowRef.current) return;

      const position = reactFlowRef.current.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      onAddNode(type, position);
    },
    [onAddNode],
  );

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const handleSelectionChange = useCallback(
    (params: OnSelectionChangeParams<ComposerNode, ComposerEdge>) => {
      const selectedNode = params.nodes[0];
      const selectedEdge = params.edges[0];
      onSelectionChange(selectedNode?.id ?? null, selectedEdge?.id ?? null);
    },
    [onSelectionChange],
  );

  return (
    <section className="cx-canvas-wrap" aria-labelledby="canvas-heading" aria-describedby="canvas-help">
      <h2 id="canvas-heading" className="cx-sr-only">Workflow canvas</h2>
      <p id="canvas-help" className="cx-sr-only">
        Drop nodes from the Blocks palette, or use the Connect button in the toolbar
        to wire nodes with a keyboard-accessible dialog. Select a node to edit it in the inspector.
      </p>
      <ReactFlow
        nodes={nodes}
        edges={styledEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onInit={(instance) => {
          reactFlowRef.current = instance as ReactFlowInstance<ComposerNode, ComposerEdge>;
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onSelectionChange={handleSelectionChange}
        nodeTypes={composerNodeTypes}
        connectionMode={ConnectionMode.Strict}
        snapToGrid
        snapGrid={[16, 16]}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        deleteKeyCode={['Backspace', 'Delete']}
        multiSelectionKeyCode={['Meta', 'Control']}
        proOptions={{ hideAttribution: true }}
        defaultEdgeOptions={{
          type: 'smoothstep',
        }}
      >
        <Background
          id="dots"
          variant={BackgroundVariant.Dots}
          gap={22}
          size={1.2}
          color="var(--cx-line-strong)"
        />
        <Controls showInteractive={false} aria-label="Canvas controls" />
        <MiniMap
          nodeColor={(n) => NODE_MINIMAP_COLORS[n.type ?? ''] ?? 'var(--cx-brand)'}
          maskColor="var(--cx-minimap-mask)"
          pannable
          zoomable
        />
      </ReactFlow>
    </section>
  );
};
